import {useMemo, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  // county persona actions and selectors
  setPersonaByCounty,
  setLoadingPersonaByCounty,
  setPersonaByCountyError,
  selectPersonaByCounty,
  selectLoadingPersonaByCounty,
  selectPersonaByCountyError,

  // congressional district persona actions and selectors
  setPersonaByCongressionalDistrict,
  setLoadingPersonaByCongressionalDistrict,
  setPersonaByCongressionalDistrictError,
  selectPersonaByCongressionalDistrict,
  selectLoadingPersonaByCongressionalDistrict,
  selectPersonaByCongressionalDistrictError,

  // census tract persona actions and selectors
  setPersonaByCensusTract,
  setLoadingPersonaByCensusTract,
  setPersonaByCensusTractError,
  selectPersonaByCensusTract,
  selectLoadingPersonaByCensusTract,
  selectPersonaByCensusTractError
} from '../slices/personaMapDataSlice'
import useQueryPersonaByCounty from '../../api/queries/persona/PersonaByCountyQuery'
import queryPersonaByCongressionalDistrict from '../../api/queries/persona/PersonaByCongressionalDistrictQuery'
import queryPersonaByCensusTract from '../../api/queries/persona/personaByCensusTract'

export const usePersonaData = () => {
  const dispatch = useDispatch()

  // Get data from Redux store
  const personaByCounty = useSelector(selectPersonaByCounty)
  const loadingPersonaByCounty = useSelector(selectLoadingPersonaByCounty)
  const personaByCountyError = useSelector(selectPersonaByCountyError)

  // Execute GraphQL query
  const {
    personaByCounty: queryPersonaByCountyData,
    loadingPersonaByCounty: queryLoadingPersonaByCounty,
    personaByCountyError: queryPersonaByCountyError,
    refetchPersonaByCounty
  } = useQueryPersonaByCounty()

  // Sync query results to Redux store
  useEffect(() => {
    dispatch(setLoadingPersonaByCounty(queryLoadingPersonaByCounty))
  }, [queryLoadingPersonaByCounty, dispatch])

  useEffect(() => {
    if (queryPersonaByCountyData !== undefined) {
      dispatch(setPersonaByCounty(queryPersonaByCountyData))
    }
  }, [queryPersonaByCountyData, dispatch])

  useEffect(() => {
    if (queryPersonaByCountyError !== undefined) {
      dispatch(setPersonaByCountyError(queryPersonaByCountyError))
    }
  }, [queryPersonaByCountyError, dispatch])

  // Compute derived data
  const personaByState = useMemo(() => {
    const statePersona = personaByCounty?.countyPersona?.personaPerState || []
    return {statePersona}
  }, [personaByCounty])

  const personaPercentage = useMemo(() => {
    return {personaPercentage: personaByCounty?.countyPersona?.personaPercentage}
  }, [personaByCounty])

  const countyPersona = useMemo(() => {
    return personaByCounty?.countyPersona?.personaPerCounty || []
  }, [personaByCounty])

  const countyFipsToPersona = useMemo(() => {
    const map = {}
    for (const item of countyPersona ?? []) {
      map[item.fips] = item.dominant_persona
    }
    return map
  }, [countyPersona])

  const countyFipsToProperties = useMemo(() => {
    const map = {}
    for (const item of countyPersona ?? []) {
      const {fips, ...properties} = item
      map[fips] = properties
    }
    return map
  }, [countyPersona])

  const stateFipsToPersona = useMemo(() => {
    const map = {}
    for (const item of personaByState?.statePersona ?? []) {
      map[item.stateFips] = item.dominant_persona
    }
    return map
  }, [personaByState])

  const stateFipsToProperties = useMemo(() => {
    const map = {}
    for (const item of personaByState?.statePersona ?? []) {
      const {stateFips, ...properties} = item
      map[stateFips] = properties
    }
    return map
  }, [personaByState])

  return useMemo(
    () => ({
      loading: loadingPersonaByCounty,
      personaByCounty: personaByCounty?.countyPersona,
      personaByState,
      personaPercentage,
      countyFipsToPersona,
      countyFipsToProperties,
      stateFipsToPersona,
      stateFipsToProperties,
      error: personaByCountyError,
      refetch: refetchPersonaByCounty
    }),
    [
      loadingPersonaByCounty,
      personaByCounty,
      personaByState,
      personaPercentage,
      countyFipsToPersona,
      countyFipsToProperties,
      stateFipsToPersona,
      stateFipsToProperties,
      personaByCountyError,
      refetchPersonaByCounty
    ]
  )
}

export const usePersonaByCongressionalDistrict = () => {
  const dispatch = useDispatch()

  // Get data from Redux store
  const personaByCongressionalDistrict = useSelector(selectPersonaByCongressionalDistrict)
  const loadingPersonaByCongressionalDistrict = useSelector(selectLoadingPersonaByCongressionalDistrict)
  const personaByCongressionalDistrictError = useSelector(selectPersonaByCongressionalDistrictError)

  // Execute GraphQL query
  const {
    personaByCongressionalDistrict: queryPersonaByCongressionalDistrictData,
    loadingPersonaByCongressionalDistrict: queryLoadingPersonaByCongressionalDistrict,
    personaByCongressionalDistrictError: queryPersonaByCongressionalDistrictError,
    refetchPersonaByCongressionalDistrict
  } = queryPersonaByCongressionalDistrict()

  // Sync query results to Redux store
  useEffect(() => {
    dispatch(setLoadingPersonaByCongressionalDistrict(queryLoadingPersonaByCongressionalDistrict))
  }, [queryLoadingPersonaByCongressionalDistrict, dispatch])

  useEffect(() => {
    if (queryPersonaByCongressionalDistrictData !== undefined) {
      dispatch(setPersonaByCongressionalDistrict(queryPersonaByCongressionalDistrictData))
    }
  }, [queryPersonaByCongressionalDistrictData, dispatch])

  useEffect(() => {
    if (queryPersonaByCongressionalDistrictError !== undefined) {
      dispatch(setPersonaByCongressionalDistrictError(queryPersonaByCongressionalDistrictError))
    }
  }, [queryPersonaByCongressionalDistrictError, dispatch])

  // Compute derived data
  const congressionalDistrictFipsToPersona = useMemo(() => {
    if (loadingPersonaByCongressionalDistrict) return false
    const mapping = {}

    for (const item of personaByCongressionalDistrict?.personaCongressionalDistrict?.personaCongressionalDistrict ??
      []) {
      mapping[item.congressional_district_id] = item.dominant_persona
    }
    return mapping
  }, [personaByCongressionalDistrict, loadingPersonaByCongressionalDistrict])

  const congressionalDistrictFipsToProperties = useMemo(() => {
    if (loadingPersonaByCongressionalDistrict) return false
    const mapping = {}
    for (const item of personaByCongressionalDistrict?.personaCongressionalDistrict?.personaCongressionalDistrict ??
      []) {
      const {congressional_district_id: fips, ...properties} = item
      mapping[fips] = properties
    }
    return mapping
  }, [personaByCongressionalDistrict, loadingPersonaByCongressionalDistrict])

  return useMemo(
    () => ({
      loading: loadingPersonaByCongressionalDistrict,
      personaByCongressionalDistrict,
      congressionalDistrictFipsToPersona,
      congressionalDistrictFipsToProperties,
      error: personaByCongressionalDistrictError,
      refetch: refetchPersonaByCongressionalDistrict
    }),
    [
      loadingPersonaByCongressionalDistrict,
      personaByCongressionalDistrict,
      congressionalDistrictFipsToPersona,
      congressionalDistrictFipsToProperties,
      personaByCongressionalDistrictError,
      refetchPersonaByCongressionalDistrict
    ]
  )
}

export const usePersonaByCensusTract = () => {
  const dispatch = useDispatch()

  // Get data from Redux store
  const personaByCensusTract = useSelector(selectPersonaByCensusTract)
  const loadingPersonaByCensusTract = useSelector(selectLoadingPersonaByCensusTract)
  const personaByCensusTractError = useSelector(selectPersonaByCensusTractError)

  // Execute GraphQL query
  const {
    personaByCensusTract: queryPersonaByCensusTractData,
    loadingPersonaByCensusTract: queryLoadingPersonaByCensusTract,
    personaByCensusTractError: queryPersonaByCensusTractError,
    refetchPersonaByCensusTract
  } = queryPersonaByCensusTract()

  // Sync query results to Redux store
  useEffect(() => {
    dispatch(setLoadingPersonaByCensusTract(queryLoadingPersonaByCensusTract))
  }, [queryLoadingPersonaByCensusTract, dispatch])

  useEffect(() => {
    if (queryPersonaByCensusTractData !== undefined) {
      dispatch(setPersonaByCensusTract(queryPersonaByCensusTractData))
    }
  }, [queryPersonaByCensusTractData, dispatch])

  useEffect(() => {
    if (queryPersonaByCensusTractError !== undefined) {
      dispatch(setPersonaByCensusTractError(queryPersonaByCensusTractError))
    }
  }, [queryPersonaByCensusTractError, dispatch])

  /*
    Structure is as follows:
    {
      'census_tract_geo_id_1': 'dominant_persona_1',
      'census_tract_geo_id_2': 'dominant_persona_2',
      ...
    }
    Above structure allows for quick lookup of dominant persona by census tract FIPS code.
  */

  const censusTractFipsToPersona = useMemo(() => {
    if (loadingPersonaByCensusTract || !personaByCensusTract) return {}
    const mapping = {}

    for (const item of personaByCensusTract?.personaCensusTract?.personaCensusTract ?? []) {
      // Use the field name from your GraphQL query
      mapping[item.censusTractGeoId] = item.dominant_persona
    }
    return mapping
  }, [personaByCensusTract, loadingPersonaByCensusTract])

  const censusTractFipsToProperties = useMemo(() => {
    if (loadingPersonaByCensusTract || !personaByCensusTract) return {}
    const mapping = {}
    for (const item of personaByCensusTract?.personaCensusTract?.personaCensusTract ?? []) {
      const {censusTractGeoId: fips, ...properties} = item
      mapping[fips] = properties
    }
    return mapping
  }, [personaByCensusTract, loadingPersonaByCensusTract])

  return useMemo(
    () => ({
      loading: loadingPersonaByCensusTract,
      censusTractFipsToPersona,
      censusTractFipsToProperties,
      personaByCensusTract,
      error: personaByCensusTractError,
      refetch: refetchPersonaByCensusTract
    }),
    [
      loadingPersonaByCensusTract,
      censusTractFipsToPersona,
      censusTractFipsToProperties,
      personaByCensusTract,
      personaByCensusTractError,
      refetchPersonaByCensusTract
    ]
  )
}
